import React, {
  useState,
  useEffect
} from "react";

import axios from "axios";

import "./Improve.css";

import VisionNavbar from "./VisionNavbar";

import PerformanceGraphs from "./PerformanceGraphs";


const Improve = () => {

  const email =
    localStorage.getItem(
      "user_email"
    );


  /* =====================================================
     HIDE MAIN NAVBAR / FOOTER
  ===================================================== */

  useEffect(() => {

    const navbar =
      document.querySelector(
        "header, .navbar"
      );

    const footer =
      document.querySelector(
        "footer"
      );


    if (navbar)
      navbar.style.display = "none";

    if (footer)
      footer.style.display = "none";


    return () => {

      if (navbar)
        navbar.style.display = "";

      if (footer)
        footer.style.display = "";

    };

  }, []);


  /* =====================================================
     STATES
  ===================================================== */

  const [
    dashboard,
    setDashboard
  ] = useState(null);


  const [
    journey,
    setJourney
  ] = useState(null);


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    showAssessment,
    setShowAssessment
  ] = useState(false);


  const [
    progressStory,
    setProgressStory
  ] = useState(false);


  const [
    dailyPracticeStreak,
    setDailyPracticeStreak
  ] = useState(false);


  const [
    history,
    setHistory
  ] = useState({});


  /* =====================================================
     FETCH DATA
  ===================================================== */

  const fetchData = async () => {

    try {

      setLoading(true);


      const [
        dashRes,
        journeyRes,
        historyRes
      ] = await Promise.all([

        axios.get(
          `/api/dashboard/${email}`
        ),

        axios.get(
          `/api/journey/${email}`
        ),

        axios.get(
          `/api/admin/player-history/${email}`
        )

      ]);


      setDashboard(
        dashRes.data
      );


      setJourney(
        journeyRes.data?.journey ||
        journeyRes.data
      );


      setHistory(
        historyRes.data
      );

    }

    catch (err) {

      console.error(
        "Improve page error:",
        err
      );

    }

    finally {

      setLoading(false);

    }

  };


  useEffect(() => {

    if (email) {

      fetchData();

    }

  }, [email]);


  /* =====================================================
     LOAD LEVEL 3 MANUAL TASKS
  ===================================================== */

  useEffect(() => {

    if (!journey)
      return;


    setProgressStory(
      journey?.level3?.progressStory ||
      false
    );


    setDailyPracticeStreak(
      journey?.level3?.dailyPracticeStreak ||
      false
    );

  }, [journey]);


  /* =====================================================
     UPDATE LEVEL 3 TASK
  ===================================================== */

  const updateLevel3Task = async (
    field,
    value
  ) => {

    try {

      await axios.patch(

        "/api/journey/update",

        {

          email,

          section: "level3",

          field,

          value

        }

      );


      await fetchData();

    }

    catch (err) {

      console.error(
        "Level 3 update error:",
        err
      );

    }

  };


  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {

    return (

      <div className="improve-loading">

        Loading...

      </div>

    );

  }


  /* =====================================================
     LEVEL DATA
  ===================================================== */

  const dailyStreak =
    dashboard?.dailyStreak || 0;


  const level = 3;


  const currentLevel =
    "Adapter";


  /* =====================================================
     LEVEL 2 COMPLETION CHECK
  ===================================================== */

  const level2Completed =

    journey?.level2?.eyeHealthCourse === true &&

    journey?.level2?.practiceDays >= 30 &&

    journey?.level2?.communityCallsCount >= 2 &&

    journey?.level2?.firstProgressCheck === true;


  /* =====================================================
     LEVEL 3 TASKS
  ===================================================== */

  const improveMetric =

    (
      journey?.level3?.improvedMetrics ||
      []
    ).length > 0;


  const evaluation60Days =

    journey?.level3?.evaluation60Days ||
    false;


  const metricImproved =

    (
      journey?.level3?.improvedMetrics ||
      []
    ).length;


  const tasks = [

    {
      label:
        "Improve One Metric",

      completed:
        improveMetric,

      manual:
        false
    },

    {
      label:
        "60 Day Evaluation",

      completed:
        evaluation60Days,

      manual:
        false
    },

    {
      label:
        "Share Progress Story",

      completed:
        progressStory,

      manual:
        true
    },

    {
      label:
        "Daily Practice Streak",

      completed:
        dailyPracticeStreak,

      manual:
        true
    }

  ];


  /* =====================================================
     PROGRESS
  ===================================================== */

  const completedTasks =

    tasks.filter(
      task =>
        task.completed
    ).length;


  const progressPercent =

    Math.round(

      (
        completedTasks /
        tasks.length
      ) * 100

    );


  /* =====================================================
     IMPROVEMENT TARGETS
  ===================================================== */

  const improvementTargets = [

    {
      name:
        "Visual Acuity",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Visual Acuity"
        )?.score || "-",

      target:
        "6/6",

      need:
        "Improve Vision"
    },

    {
      name:
        "Contrast Sensitivity",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Contrast Sensitivity"
        )?.score || "-",

      target:
        "2.0",

      need:
        "+0.5"
    },

    {
      name:
        "Reaction Speed",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Reaction Speed"
        )?.score || "-",

      target:
        "100 ms Faster",

      need:
        "Improve Speed"
    },

    {
      name:
        "Complex Reaction",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Complex Reaction"
        )?.score || "-",

      target:
        "100 ms Faster",

      need:
        "Improve Speed"
    },

    {
      name:
        "Search Span",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Search Span"
        )?.score || "-",

      target:
        "+5",

      need:
        "Increase Score"
    },

    {
      name:
        "Working Memory",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Working Memory"
        )?.score || "-",

      target:
        "+5",

      need:
        "Increase Level"
    },

    {
      name:
        "Short Term Memory",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Short Term Memory"
        )?.score || "-",

      target:
        "+5",

      need:
        "Increase Level"
    },

    {
      name:
        "Color Vision",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Color Vision"
        )?.score || "-",

      target:
        "20/20",

      need:
        "Maintain"
    },

    {
      name:
        "Anticipation",

      current:
        dashboard?.tests?.find(
          t =>
            t.name ===
            "Anticipation"
        )?.score || "-",

      target:
        "+5",

      need:
        "Increase Score"
    }

  ];


  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <div className="improve-page">


      <VisionNavbar
        active="improve"
      />


      {/* =================================================
          HERO
      ================================================= */}

      <section className="improve-hero">


        {/* ================= LEFT ================= */}

        <div className="improve-left">


          


          <h1>

            Improve Your Performance

          </h1>


          <p>

            Complete Level 3 requirements
            and unlock Thriver.

          </p>


          {/* ================= STATUS CARD ================= */}

          <div className="improve-status-card">


            <span className="status-label">

              CURRENT STATUS

            </span>


            <h2>

              Level 3 Adapter

            </h2>


            <p>

              Complete all requirements
              to unlock the next level.

            </p>


            {/* ================= REQUIREMENTS ================= */}

            <div className="requirements">

              {tasks.map(

                (task, index) => (

                  <div

                    key={index}

                    className={`requirement-item ${
                      task.manual &&
                      !level2Completed
                        ? "task-locked"
                        : ""
                    }`}

                  >

                    <div className="requirement-left">


                      {task.manual ? (

                        <>

                          <input

                            type="checkbox"

                            checked={
                              task.completed
                            }

                            disabled={
                              !level2Completed
                            }

                            onChange={e => {

                              if (

                                task.label ===
                                "Share Progress Story"

                              ) {

                                const value =
                                  e.target.checked;


                                setProgressStory(
                                  value
                                );


                                updateLevel3Task(

                                  "progressStory",

                                  value

                                );

                              }


                              if (

                                task.label ===
                                "Daily Practice Streak"

                              ) {

                                const value =
                                  e.target.checked;


                                setDailyPracticeStreak(
                                  value
                                );


                                updateLevel3Task(

                                  "dailyPracticeStreak",

                                  value

                                );

                              }

                            }}

                          />


                          {!level2Completed && (

                            <span className="lock-icon">

                              🔒

                            </span>

                          )}

                        </>

                      ) : (

                        <span className="tick-icon">

                          {task.completed
                            ? "✅"
                            : "⏳"}

                        </span>

                      )}


                      <span>

                        {task.label}

                      </span>


                    </div>


                    <div>

                      {task.completed ? (

                        <span className="complete-badge">

                          Completed

                        </span>

                      ) : (

                        <span className="pending-badge">

                          {task.manual &&
                          !level2Completed
                            ? "Locked"
                            : "Pending"}

                        </span>

                      )}

                    </div>


                  </div>

                )

              )}

            </div>


            {/* =================================================
                LEVEL 2 LOCK MESSAGE
            ================================================= */}

            {!level2Completed && (

              <div className="level2-lock-message">

                🔒 Complete Level 2 requirements
                to unlock Level 3 manual tasks.

              </div>

            )}


            {/* ================= PROGRESS ================= */}

            <div className="progress-section">

              <div className="progress-track">

                <div

                  className="progress-fill"

                  style={{
                    width:
                      `${progressPercent}%`
                  }}

                />

              </div>


              <div className="progress-footer">

                <span>

                  {completedTasks}/
                  {tasks.length}
                  {" "}
                  Complete

                </span>


                <span>

                  {progressPercent}%

                </span>

              </div>

            </div>


            {/* ================= ASSESSMENT ================= */}

            <button

              className="improve-main-btn"

              onClick={() =>
                setShowAssessment(true)
              }

            >

              IMPROVE ASSESSMENT

            </button>


          </div>

        </div>


        {/* ================= RIGHT SIDE ================= */}

        <div className="improve-right">


          {/* DAILY STREAK */}

          <div className="stat-card">

            <div className="stat-icon">

              🔥

            </div>


            <h3>

              {dailyStreak}

            </h3>


            <p>

              Daily Streak

            </p>

          </div>


          {/* CURRENT LEVEL */}

          <div className="stat-card">

            <div className="stat-icon">

              🏆

            </div>


            <h3>

              {currentLevel}

            </h3>


            <p>

              Current Level

            </p>

          </div>


          {/* METRICS IMPROVED */}

          <div className="stat-card">

            <div className="stat-icon">

              📈

            </div>


            <h3>

              {metricImproved}

            </h3>


            <p>

              Metrics Improved

            </p>

          </div>


        </div>


      </section>


      {/* =================================================
          PERFORMANCE GRAPHS
      ================================================= */}

      <PerformanceGraphs
        history={history}
      />


      {/* =================================================
          ASSESSMENT MODAL
      ================================================= */}

      {showAssessment && (

        <div
          className="assessment-overlay"
          onClick={() =>
            setShowAssessment(false)
          }
        >

          <div
            className="assessment-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >


            {/* ================= HEADER ================= */}

            <div className="assessment-header">

              <h2>

                Choose Assessment

              </h2>


              <button

                className="close-btn"

                type="button"

                onClick={() =>
                  setShowAssessment(false)
                }

              >

                ×

              </button>

            </div>


            {/* ================= GRID ================= */}

            <div className="assessment-grid">


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/visual-acuity"
                }
              >

                👁️ Visual Acuity

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/color-vision"
                }
              >

                🎨 Color Vision

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/contrast"
                }
              >

                ⚫ Contrast Sensitivity

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/reactiongame"
                }
              >

                ⚡ Reaction Speed

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/complex-reaction"
                }
              >

                🚀 Complex Reaction

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/short-term-memory"
                }
              >

                🧠 Short Term Memory

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/working-memory"
                }
              >

                📚 Working Memory

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/search-span"
                }
              >

                🔍 Search Span

              </button>


              <button
                type="button"
                onClick={() =>
                  window.location.href =
                    "/anticipation"
                }
              >

                🎯 Anticipation

              </button>


            </div>

          </div>

        </div>

      )}

    </div>

  );

};


export default Improve;